import React, { useMemo } from "react";
import { PropertiesPanel } from "./PropertiesPanel";
import { usePermissions, useHasPermission } from "@/contexts/PermissionContext";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import {
  PermissionAction,
  ResourceType,
  ComponentPermissionType,
} from "@/types/permissions";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Shield, Lock, Eye } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  WARNING,
  INFO,
} from "@/styles/tokens";
import {
  getViewerPermissionIdString,
  getViewerPermissionIdStrings,
} from "@/components/permissions/viewerPermissionIds";

interface PermissionAwarePropertiesPanelProps {
  className?: string;
  onSelectComponentTypeForSketch?: () => void;
}

export const PermissionAwarePropertiesPanel: React.FC<
  PermissionAwarePropertiesPanelProps
> = ({ className, onSelectComponentTypeForSketch }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { getComponentPermissions } = usePermissions();
  const { getSelectedComponents, project } = useEditorStore();
  const user = useAuthStore((state) => state.user);

  const selectedComponentObjects = getSelectedComponents();
  const singleSelectedComponent =
    selectedComponentObjects.length === 1 ? selectedComponentObjects[0] : null;
  const singleSelectedComponentId = singleSelectedComponent?.id;

  const currentUserIds = useMemo(
    () => getViewerPermissionIdStrings(user?.id, user?._id),
    [user?.id, user?._id],
  );
  const projectOwnerIdStr =
    getViewerPermissionIdString(project?.ownerId) ??
    getViewerPermissionIdString(project?.owner_id);
  const isProjectOwner = !!(
    projectOwnerIdStr && currentUserIds.has(projectOwnerIdStr)
  );
  const userMembership = project?.members?.find((m) => {
    const memberUserIdStr = getViewerPermissionIdString(m.userId);
    return !!(memberUserIdStr && currentUserIds.has(memberUserIdStr));
  });
  const hasProjectEditAccess =
    isProjectOwner ||
    (userMembership && ["owner", "admin", "editor"].includes(userMembership.role));

  // Check component-level permissions with project context
  const componentPermissions = useMemo(() => {
    if (!singleSelectedComponentId) return null;
    return getComponentPermissions(singleSelectedComponentId, {
      ownerId: project?.ownerId,
      owner_id: project?.owner_id,
      members: project?.members,
    });
  }, [
    singleSelectedComponentId,
    getComponentPermissions,
    project?.ownerId,
    project?.owner_id,
    project?.members,
  ]);

  // Check project-level permissions (API check with local fallback)
  const apiCanEditProject = useHasPermission(
    { type: ResourceType.PROJECT, id: projectId! },
    PermissionAction.UPDATE,
  );

  // User can edit if API says yes OR if they have project-level access (owner/editor)
  const canEditProject = apiCanEditProject || hasProjectEditAccess;

  // Determine what permissions the user has for the selected component
  const canEditComponent =
    singleSelectedComponent && componentPermissions
      ? componentPermissions[ComponentPermissionType.UPDATE]?.allowed !== false
      : canEditProject;

  const canEditContent =
    singleSelectedComponent && componentPermissions
      ? componentPermissions[ComponentPermissionType.EDIT_CONTENT]?.allowed !==
        false
      : canEditProject;

  const canEditLayout =
    singleSelectedComponent && componentPermissions
      ? componentPermissions[ComponentPermissionType.EDIT_LAYOUT]?.allowed !==
        false
      : canEditProject;

  const canEditStyle =
    singleSelectedComponent && componentPermissions
      ? componentPermissions[ComponentPermissionType.STYLE]?.allowed !== false
      : canEditProject;

  const canDelete =
    singleSelectedComponent && componentPermissions
      ? componentPermissions[ComponentPermissionType.DELETE]?.allowed !== false
      : canEditProject;

  // Get permission restrictions to show in UI
  const getPermissionRestrictions = () => {
    const restrictions = [];

    if (!canEditComponent) {
      restrictions.push("Cannot edit component properties");
    }
    if (!canEditContent && canEditComponent) {
      restrictions.push("Cannot edit content");
    }
    if (!canEditLayout && canEditComponent) {
      restrictions.push("Cannot edit layout properties");
    }
    if (!canEditStyle && canEditComponent) {
      restrictions.push("Cannot edit styling");
    }
    if (!canDelete && canEditComponent) {
      restrictions.push("Cannot delete component");
    }

    return restrictions;
  };

  const restrictions = getPermissionRestrictions();

  // If no edit permissions at all, show read-only view
  if (!canEditProject && !canEditComponent) {
    return (
      <Card
        className={className}
        style={{
          backgroundColor: `hsl(var(--background))`,
          border: `1px solid hsl(var(--border))`,
          borderRadius: '8px',
        }}
      >
        <div style={{ padding: '16px' }}>
          <div className="flex items-center gap-2" style={{ marginBottom: '16px' }}>
            <Eye className="w-4 h-4" style={{ color: `hsl(var(--muted-foreground))` }} />
            <h3
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: `hsl(var(--foreground))`,
                margin: 0,
              }}
            >
              Properties (Read-Only)
            </h3>
          </div>

          <Alert
            style={{
              backgroundColor: INFO.bg,
              border: `1px solid ${INFO.border}`,
              borderRadius: '6px',
            }}
          >
            <Shield className="w-4 h-4" style={{ color: INFO.icon }} />
            <AlertDescription style={{ color: INFO.text, fontSize: '13px' }}>
              You can view component properties but cannot make changes. Contact
              the project owner for edit access.
            </AlertDescription>
          </Alert>

          {/* Show read-only properties panel */}
          <div style={{ marginTop: '16px', opacity: 0.6, pointerEvents: 'none' }}>
            <PropertiesPanel onSelectComponentTypeForSketch={onSelectComponentTypeForSketch} />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Show permission warnings if there are restrictions */}
      {restrictions.length > 0 && (
        <Alert
          style={{
            marginBottom: '16px',
            flexShrink: 0,
            backgroundColor: WARNING.bg,
            border: `1px solid ${WARNING.border}`,
            borderRadius: '6px',
            margin: '8px',
          }}
        >
          <Lock className="w-4 h-4" style={{ color: WARNING.icon }} />
          <AlertDescription>
            <div style={{ fontSize: '13px' }}>
              <p style={{ fontWeight: 500, marginBottom: '4px', color: WARNING.text }}>
                Limited permissions:
              </p>
              <ul style={{ fontSize: '12px', margin: 0, paddingLeft: '16px' }}>
                {restrictions.map((restriction, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-1"
                    style={{ color: `hsl(var(--muted-foreground))`, marginBottom: '2px' }}
                  >
                    <span
                      style={{
                        width: '4px',
                        height: '4px',
                        backgroundColor: WARNING.icon,
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}
                    />
                    {restriction}
                  </li>
                ))}
              </ul>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Enhanced Properties Panel with permission context */}
      <div className="flex-1 min-h-0">
        <PropertiesPanel onSelectComponentTypeForSketch={onSelectComponentTypeForSketch} />
      </div>
    </div>
  );
};
